import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../../recoil/atoms/opcInfoAtom';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAsyncThrow } from '../../hooks/useAsyncThrow';
import { useSaveCoverableInformation } from '../../hooks/useSaveCoverableInformation';
import { jobInfoAtom } from '../../recoil/atoms/jobInfoAtom';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type { ActionType, HandleCloseOPCModal } from './types';
import ExistingCoverage from './components/ExistingCoverage/ExistingCoverage';
import PetHealthyLastSixMonthsOPC from './components/PetHealthyLastSixMonthsOPC';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import usePageNavigation from '../../hooks/usePageNavigation';
import ExistingShoppingBasketModal from '../../shared_components/ExistingShoppingBasketModal/ExistingShoppingBasketModal';
import OpcModal from './components/OpcModal';
import { callMeVariantOpcAtom } from '../../recoil/atoms/callMeVariantOpcAtom';
import { useHippoText } from '../../hooks/useHippoText';
import { infoModalVariantOpcAtom } from '../../recoil/atoms/infoModalVariantOpcAtom';
import type { ViewInformationModalProps } from '../../config/modalConfig';
import useOpcJob from '../../hooks/useOpcJob';
import ViewInformationModal from '../../shared_components/ViewInformationModal/ViewInformationModal';
import { isOpcFetchingAtom } from '../../recoil/atoms/isOpcFetchingAtom';

interface CallMeModalProps {
    title?: string;
    description?: string;
    activityCode: string;
    isDefault: boolean;
    closeModal: () => void;
    closeConfirmationModal: () => void;
    overlapseModalText?: Record<string, string>;
}

const OpcEntryPage: WizardPageComponent = () => {
    const opcInfo = useRecoilValue(opcInfoAtom);
    const getHippoText = useHippoText();
    const { tilpas, productName, coverageId, adjustAction, isFromDAFlow } = opcInfo;
    const { isExistingBasket, jobNumber, sessionId } = useRecoilValue(jobInfoAtom);
    const { asyncThrow } = useAsyncThrow();
    const { goBackToDetailsPage } = usePageNavigation();
    const { withdrawOPCShoppingBasket } = useOpcJob();
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const isEditCoveragePage = tilpas === 'tilpas';
    const [showPetHealthySixMonths, setShowPetHealthySixMonths] = useState(
        (productName === 'topdanmark_dog' || productName === 'topdanmark_cat') && !isFromDAFlow
    );
    const [isOpenModal, setIsOpenModal] = useState(
        (!isEditCoveragePage && !isExistingBasket) || !!coverageId
    );
    const [isOpenExistingShoppingBasketModal, setIsOpenExistingShoppingBasketModal] = useState(
        isExistingBasket && !isEditCoveragePage
    );
    const [callMeVariant, setCallMeVariant] = useRecoilState(callMeVariantOpcAtom);
    const [informationModalVariant, setInformationModalVariant] =
        useRecoilState(infoModalVariantOpcAtom);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const petHealthInfoRef = useRef<CoverableInformationToUpdateDTO>();
    const { goToShoppingBasket } = usePageNavigation();
    const existingBasket = useMemo(
        () => (isExistingBasket ? { jobNumber, sessionId } : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const shouldOpenAdjustCoverageModalImmediately = useMemo(() => !!adjustAction, []);

    const showEditCoverage = useCallback((): boolean => {
        return (
            isEditCoveragePage &&
            !showPetHealthySixMonths &&
            !shouldOpenAdjustCoverageModalImmediately
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPetHealthySixMonths]);

    const showExistingShoppingBasketModal = useCallback((): boolean => {
        return !!existingBasket && !showPetHealthySixMonths;
    }, [existingBasket, showPetHealthySixMonths]);

    const showOpcModal = useCallback((): boolean => {
        return !isOpenExistingShoppingBasketModal && isOpenModal && !showPetHealthySixMonths;
    }, [isOpenExistingShoppingBasketModal, isOpenModal, showPetHealthySixMonths]);

    const handleCloseModal: HandleCloseOPCModal = (
        closeCallback?: () => Promise<void> | void,
        actionType?: ActionType
    ): void => {
        if (!closeCallback) {
            setIsOpenModal(false);
            return;
        }

        // Handle special event if customers want to go to Shopping basket
        // with their new transaction after OPC modal
        if (actionType === 'goToShoppingBasket') {
            // Calling save pet heathy here for saving with correct job
            const petHealthInfo = petHealthInfoRef.current;
            if (petHealthInfo) {
                void saveCoverableInformation(petHealthInfo);
            }
            if (existingBasket) {
                void withdrawOPCShoppingBasket(existingBasket.jobNumber, existingBasket.sessionId);
            }
            setIsOpcFetching(true);
            /* Flos dialog modal is out of control of react suspense
            closeCallback block UI re-render so isOpcFetching === false until closeCallback done
            queueMicrotask help to queue closeCallback until UI re-render with isOpcFetching === true */
            queueMicrotask(() => {
                void closeCallback();
            });
            return;
        }

        const closeCallbackExecute = closeCallback();
        if (closeCallbackExecute instanceof Promise) {
            closeCallbackExecute
                .then(() => {
                    setIsOpenModal(false);
                })
                .catch((e) => {
                    asyncThrow(e);
                });
            return;
        }
        setIsOpenModal(false);
    };

    useMemo(() => {
        if (callMeVariant) {
            setIsOpenModal(false);
            setIsOpenExistingShoppingBasketModal(false);
        }
    }, [callMeVariant]);

    useMemo(() => {
        if (informationModalVariant) {
            setIsOpenModal(false);
        }
    }, [informationModalVariant]);

    const getCallMeModalProps = useCallback((): CallMeModalProps => {
        const backToPreviousPage = (): void => {
            setCallMeVariant(undefined);
            if (isFromDAFlow) {
                goToShoppingBasket();
            } else {
                goBackToDetailsPage();
            }
        };
        const defaultProps: CallMeModalProps = {
            title: undefined,
            description: undefined,
            activityCode: 'dss_call_request',
            isDefault: false,
            closeModal: () => {
                setCallMeVariant(undefined);
            },
            closeConfirmationModal: backToPreviousPage,
            overlapseModalText: undefined
        };
        const commonCloseCallMe = (): void => {
            setCallMeVariant(undefined);
            setIsOpenModal(true);
        };

        switch (callMeVariant) {
            case 'voluntaryAlarmNoToYes':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALLME-MODAL-PRC-ALARM-HEADER'),
                    description: getHippoText('DSS-CALLME-MODAL-PRC-ALARM-SUBHEADER'),
                    closeModal: backToPreviousPage
                };
            case 'injuryDegree':
                return {
                    ...defaultProps,
                    title: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-INJURY-DEGREE-SUMINSURED-CALL-ME-HEADER'
                    ),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-INJURY-DEGREE-SUMINSURED-CALL-ME'
                    ),
                    overlapseModalText: {
                        modalChooseTimeSubHeader:
                            'Vælg et tidspunkt, så ringer vi dig op og ordner det over telefonen.'
                    },
                    closeModal: commonCloseCallMe
                };
            case 'employmentNotInList':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-HEADER'),
                    description: getHippoText('DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-SUBHEADER'),
                    closeModal: commonCloseCallMe
                };
            case 'hazardAboveFiveEmployment':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-EMPLOYMENTHAZARD-CALL-ME-HEADER'),
                    description: getHippoText('DSS-EMPLOYMENTHAZARD-CALL-ME'),
                    closeModal: commonCloseCallMe
                };
            case 'professionalSportEmployment':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-ACCIMMEDIATECOMPENSATIONCOV-SUMINSURED-CALL-ME'
                    ),
                    closeModal: backToPreviousPage
                };
            case 'professionalSportEmploymentAddImmediateComp':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-ACCIMMEDIATECOMPENSATIONCOV-SUMINSURED-CALL-ME'
                    ),
                    closeModal: backToPreviousPage
                };
            case 'extendedToStandard':
                return {
                    ...defaultProps,
                    title: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-CONTRACT-TYPE-SUMINSURED-CALL-ME-HEADER'
                    ),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-CONTRACT-TYPE-SUMINSURED-CALL-ME'
                    ),
                    closeModal: commonCloseCallMe
                };
            case 'fulltimeToLeisure':
                return {
                    ...defaultProps,
                    title: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-FULLTIME-OR-LEISURE-SUMINSURED-CALL-ME-HEADER'
                    ),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-FULLTIME-OR-LEISURE-SUMINSURED-CALL-ME'
                    ),
                    closeModal: commonCloseCallMe
                };
            case 'traNumberInHousehold':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALL-ME-MODAL-DOWNGRADE-TRAVEL-TITLE'),
                    description: getHippoText('DSS-CALL-ME-MODAL-DOWNGRADE-TRAVEL-DESCRIPTION'),
                    overlapseModalText: {
                        modalChooseTimeSubHeader: '',
                        modalContactedPrompt: 'Hvornår skal vi ringe?'
                    },
                    closeModal: backToPreviousPage
                };
            case 'childTurn18Within40days':
                return {
                    ...defaultProps,
                    title: 'Bestil et opkald',
                    description: getHippoText('DSS-ADDITIONAL-INFORMATION- CHILD-AGE-VALIDATION'),
                    closeModal: backToPreviousPage
                };
            case 'youngTurn28Within40days':
                return {
                    ...defaultProps,
                    title: 'Bestil et opkald',
                    description: getHippoText('DSS-ACC-YOUNG-TOO-OLD-CALL-ME'),
                    closeModal: backToPreviousPage
                };
            case 'deathCovExceedLimit':
                return {
                    ...defaultProps,
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Bestil et opkald',
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-ACC-DEATH-COV-SUMINSURED-CALL-ME'
                    ),
                    closeModal: backToPreviousPage
                };
            case 'addDeathCov':
                return {
                    ...defaultProps,
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Du kan ikke købe dækningen dødsfaldsdækningen online',
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-ACCDEATHCOV-SUMINSURED-CALL-ME'
                    ),
                    closeModal: backToPreviousPage
                };
            case 'addHighRiskOrImmediateCompCov':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
                    description: getHippoText(
                        'DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-SUMINSURED-CALL-ME'
                    ),
                    closeModal: backToPreviousPage
                };
            case 'lowerAccInsuranceSumThanPrevious':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-SUMINSURED-CALL-ME-HEADER'),
                    description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-SUMINSURED-CALL-ME'),
                    closeModal: backToPreviousPage
                };
            case 'petBetween5and7YearsOldHealth':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
                    description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
                    closeModal: backToPreviousPage
                };
            case 'petBetween5and7YearsOldHealthPlus':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
                    description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
                    closeModal: backToPreviousPage
                };
            case 'petBetween5and7YearsOldLife':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
                    description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
                    closeModal: backToPreviousPage
                };
            case 'prcInsuraceSumsTooLow':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-CALLME-MODAL-INSURANCESUMS-PRC-HEADER'),
                    description: getHippoText('DSS-CALLME-MODAL-INSURANCESUMS-PRC-BODY'),
                    closeModal: backToPreviousPage
                };
            case 'carForcedDeductible':
                return {
                    ...defaultProps,
                    closeModal: backToPreviousPage
                };
            case 'default':
            default:
                return defaultProps;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callMeVariant]);

    const getInformationModalProps = useCallback((): ViewInformationModalProps => {
        const defaultProps: ViewInformationModalProps = {
            title: '',
            description: '',
            rawHtmlDescription: ''
        };
        switch (informationModalVariant) {
            case 'adultAccFulltimeLeisure':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE-TITLE'),
                    rawHtmlDescription: getHippoText(
                        'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE'
                    )
                };
            case 'adultAccContractType':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ACC-HIGHLIGHTED-TEXT-MODAL-CONTRACTTYPE-TITLE'),
                    rawHtmlDescription: getHippoText('DSS-ACC-HIGHLIGHTED-TEXT-MODAL-CONTRACTTYPE')
                };
            case 'adultAccInjuryDegreeFivePercent':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE'),
                    rawHtmlDescription: getHippoText(
                        'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-FIVE-PERCENT'
                    )
                };
            case 'adultAccInjuryDegreeEightPercent':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE'),
                    rawHtmlDescription: getHippoText(
                        'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-EIGHT-PERCENT'
                    )
                };
            case 'traNumberInHousehold':
                return {
                    ...defaultProps,
                    title: getHippoText('DSS-TRAVEL-COVERABLE-INFO-STEP-MODAL-HEADER'),
                    rawHtmlDescription: getHippoText(
                        'DSS-TRAVEL-COVERABLE-INFO-STEP-MODAL-DESCRIPTION'
                    )
                };
            case 'accReduceInsuranceSum':
                return {
                    ...defaultProps,
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Nedsæt forsikringssum',
                    description:
                        'Før du kan nedsætte din forsikringssum, har vi brug for at tale med dig og rådgive dig omkring konsekvenserne.'
                };
            case 'default':
            default:
                return defaultProps;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [informationModalVariant]);

    const {
        title,
        description,
        activityCode,
        isDefault,
        closeModal,
        closeConfirmationModal,
        overlapseModalText
    } = getCallMeModalProps();

    const handleOpenModal = (): void => {
        if (existingBasket?.jobNumber) {
            setIsOpenExistingShoppingBasketModal(true);
            return;
        }
        setIsOpenModal(true);
    };

    if (showPetHealthySixMonths) {
        return (
            <PetHealthyLastSixMonthsOPC
                continueToOpc={(petHealthInfo) => {
                    setShowPetHealthySixMonths(false);
                    petHealthInfoRef.current = petHealthInfo;
                }}
            />
        );
    }

    return (
        <>
            {showEditCoverage() && <ExistingCoverage onOpenModal={handleOpenModal} />}
            {showExistingShoppingBasketModal() && existingBasket && (
                <ExistingShoppingBasketModal
                    existingBasket={existingBasket}
                    showModal={isOpenExistingShoppingBasketModal}
                    closeModal={(isOpenOPCModal) => {
                        setIsOpenExistingShoppingBasketModal(false);
                        setIsOpenModal(isOpenOPCModal);
                    }}
                />
            )}
            {showOpcModal() && (
                <OpcModal
                    modal={tilpas}
                    open={isOpenModal}
                    handleCloseModal={handleCloseModal}
                    shouldNavigateToShoppingBasket={shouldOpenAdjustCoverageModalImmediately}
                />
            )}
            {callMeVariant && (
                <CallMeModal
                    title={title}
                    description={description}
                    activityCode={activityCode}
                    isDefault={isDefault}
                    showModal={!!callMeVariant}
                    closeModal={closeModal}
                    closeConfirmationModal={closeConfirmationModal}
                    overlapseModalText={overlapseModalText}
                />
            )}
            {informationModalVariant && (
                <ViewInformationModal
                    showModal={!!informationModalVariant}
                    modalProps={getInformationModalProps()}
                    closeModal={() => {
                        setInformationModalVariant(undefined);
                        setIsOpenModal(true);
                    }}
                />
            )}
        </>
    );
};

export default OpcEntryPage;
